import { SyncPortError } from '../../shared/errors.js';
import { t } from './i18n/index.js';

export class ShareUnavailableError extends SyncPortError {}

export type ShareResult = 'shared' | 'cancelled';

export const canShareNatively = (nav: Pick<Navigator, 'share'>): boolean => typeof nav.share === 'function';

/**
 * Chromium only shares files whose MIME type is on its safe list (media, text, PDF) and answers
 * "Permission denied" otherwise, while canShare() still says yes. Mirror the list so the button
 * appears only when the share can succeed.
 */
export const isShareableFileType = (mimeType: string): boolean =>
  ['image/', 'video/', 'audio/', 'text/'].some((prefix) => mimeType.startsWith(prefix)) || mimeType === 'application/pdf';

/** True when the browser can put this file on the OS share sheet (Android Chrome, iOS Safari). */
export const canShareFile = (nav: Pick<Navigator, 'share' | 'canShare'>, file: File): boolean =>
  canShareNatively(nav) &&
  typeof nav.canShare === 'function' &&
  isShareableFileType(file.type) &&
  nav.canShare({ files: [file] });

export const shareLinkData = (link: string): ShareData => ({
  title: 'Sync Port',
  text: t('share.linkMessage'),
  url: link,
});

/** Opens the OS share sheet. Closing the sheet is a normal outcome, not an error. */
const runShare = async (data: ShareData): Promise<ShareResult> => {
  if (!canShareNatively(navigator)) {
    throw new ShareUnavailableError('navigator.share is not available');
  }
  try {
    await navigator.share(data);
    return 'shared';
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return 'cancelled';
    }
    throw error;
  }
};

export const shareLink = (link: string): Promise<ShareResult> => runShare(shareLinkData(link));

/** Offers the received file to other apps: "Open with", save to Files or Drive, send onwards. */
export const shareFile = (file: File): Promise<ShareResult> => runShare({ files: [file], title: file.name });
